import React, { useState, useMemo } from 'react';
import { Defect } from '../services/defectService';
import { Group } from '../services/groupService';
import TagEditor from './TagEditor';

interface DefectsTableProps {
  defects: Defect[];
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onValidationChange: (defectId: string, validation: boolean) => void;
  onBulkValidationChange?: (description: string, validation: boolean) => void;
  onSuggestionsUpdate: (defectId: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => void;
  onBulkSuggestionsUpdate?: (description: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => void;
  onDelete?: (defectId: string) => void;
  groups: Group[];
  parentGroups: string[];
  userRole?: string;
  groupByDescription?: boolean;
}

// Açıklamaya göre grupla
interface GroupedDefect extends Defect {
  count?: number;
  groupedIds?: string[];
}

const DefectsTable: React.FC<DefectsTableProps> = ({
  defects,
  onSort,
  sortColumn,
  sortDirection,
  onValidationChange,
  onBulkValidationChange,
  onSuggestionsUpdate,
  onBulkSuggestionsUpdate,
  onDelete,
  groups,
  parentGroups,
  userRole,
  groupByDescription = false,
}) => {
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  // Açıklamaya göre grupla
  const groupedDefects = useMemo(() => {
    if (!groupByDescription) return defects;

    const grouped = new Map<string, GroupedDefect>();
    
    defects.forEach(defect => {
      const key = defect.description || '';
      if (grouped.has(key)) {
        const existing = grouped.get(key)!;
        existing.count = (existing.count || 1) + 1;
        existing.groupedIds = existing.groupedIds || [(existing._id || existing.id)!];
        existing.groupedIds.push((defect._id || defect.id)!);
      } else {
        grouped.set(key, {
          ...defect,
          count: 1,
          groupedIds: [(defect._id || defect.id)!]
        });
      }
    });

    return Array.from(grouped.values());
  }, [defects, groupByDescription]);

  const handleEditStart = (defectId: string, field: string, currentValue: string) => {
    setEditingCell({ id: defectId, field });
    setEditValue(currentValue || '');
  };

  const handleEditSave = (defectId: string, field: string) => {
    if (editValue.trim() !== '') {
      const suggestions: any = {};
      suggestions[field] = editValue.trim();
      onSuggestionsUpdate(defectId, suggestions);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleTagUpdate = (defectId: string, newTags: string[]) => {
    const tagString = newTags.join(', ');
    onSuggestionsUpdate(defectId, { generatedTag: tagString });
  };

  const handleBulkTagUpdate = (description: string, newTags: string[]) => {
    if (onBulkSuggestionsUpdate) {
      const tagString = newTags.join(', ');
      onBulkSuggestionsUpdate(description, { generatedTag: tagString });
    }
  };

  const toggleDescriptionExpand = (description: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(description)) {
        newSet.delete(description);
      } else {
        newSet.add(description);
      }
      return newSet;
    });
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Tag'ları parse et (string veya array olabilir)
  const parseTags = (tag: any): string[] => {
    if (!tag) return [];
    if (Array.isArray(tag)) return tag;
    if (typeof tag === 'string') {
      return tag.split(',').map(t => t.trim()).filter(t => t.length > 0);
    }
    return [];
  };

  const formatDate = (dateStr: any) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            {groupByDescription && <th style={{ width: '50px' }}>Adet</th>}
            <th 
              className={`sortable ${sortColumn === 'description' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('description')}
              style={{ minWidth: '250px' }}
            >
              Açıklama {getSortIcon('description')}
            </th>
            <th 
              className={`sortable ${sortColumn === 'controlItemGroupName' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('controlItemGroupName')}
            >
              Grup {getSortIcon('controlItemGroupName')}
            </th>
            <th 
              className={`sortable ${sortColumn === 'parentControlItemGroupName' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('parentControlItemGroupName')}
            >
              Üst Grup {getSortIcon('parentControlItemGroupName')}
            </th>
            <th 
              className={`sortable ${sortColumn === 'generatedGroup' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('generatedGroup')}
            >
              Önerilen Grup {getSortIcon('generatedGroup')}
            </th>
            <th 
              className={`sortable ${sortColumn === 'generatedParentGroup' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('generatedParentGroup')}
            >
              Önerilen Üst Grup {getSortIcon('generatedParentGroup')}
            </th>
            <th style={{ minWidth: '180px' }}>
              Etiketler
            </th>
            <th 
              className={`sortable ${sortColumn === 'generatedAt' ? `sorted-${sortDirection}` : ''}`}
              onClick={() => onSort('generatedAt')}
            >
              Tarih {getSortIcon('generatedAt')}
            </th>
            <th>Doğrulama</th>
            {userRole === 'admin' && <th>İşlem</th>}
          </tr>
        </thead>
        <tbody>
          {groupedDefects.map((defect: any) => {
            const defectId = defect._id || defect.id;
            const tags = parseTags(defect.generatedTag);
            const isExpanded = expandedDescriptions.has(defect.description);
            
            return (
              <tr key={defectId}>
                {/* Adet Sütunu (gruplamada) */}
                {groupByDescription && (
                  <td style={{ textAlign: 'center' }}>
                    <span className="count-badge">{defect.count || 1}</span>
                  </td>
                )}

                {/* Açıklama */}
                <td>
                  <div 
                    className={`description-cell ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleDescriptionExpand(defect.description)}
                    title={defect.description}
                  >
                    {defect.description}
                  </div>
                </td>

                {/* Grup */}
                <td>
                  {editingCell?.id === defectId && editingCell?.field === 'controlItemGroupName' ? (
                    <div className="edit-cell">
                      <select
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      >
                        <option value="">Seçiniz</option>
                        {groups.map(g => (
                          <option key={g.name} value={g.name}>{g.name}</option>
                        ))}
                      </select>
                      <button onClick={() => handleEditSave(defectId, 'generatedGroup')}>✓</button>
                      <button onClick={handleEditCancel}>✕</button>
                    </div>
                  ) : (
                    <span 
                      className="editable-cell"
                      onClick={() => handleEditStart(defectId, 'controlItemGroupName', defect.controlItemGroupName)}
                    >
                      {defect.controlItemGroupName || '-'}
                    </span>
                  )}
                </td>

                {/* Üst Grup */}
                <td>
                  {defect.parentControlItemGroupName || '-'}
                </td>

                {/* Önerilen Grup */}
                <td>
                  {editingCell?.id === defectId && editingCell?.field === 'generatedGroup' ? (
                    <div className="edit-cell">
                      <select
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      >
                        <option value="">Seçiniz</option>
                        {groups.map(g => (
                          <option key={g.name} value={g.name}>{g.name}</option>
                        ))}
                      </select>
                      <button onClick={() => handleEditSave(defectId, 'generatedGroup')}>✓</button>
                      <button onClick={handleEditCancel}>✕</button>
                    </div>
                  ) : (
                    <span 
                      className="editable-cell suggested"
                      onClick={() => handleEditStart(defectId, 'generatedGroup', defect.generatedGroup)}
                    >
                      {defect.generatedGroup || '-'}
                    </span>
                  )}
                </td>

                {/* Önerilen Üst Grup */}
                <td>
                  {editingCell?.id === defectId && editingCell?.field === 'generatedParentGroup' ? (
                    <div className="edit-cell">
                      <select
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      >
                        <option value="">Seçiniz</option>
                        {parentGroups.map(pg => (
                          <option key={pg} value={pg}>{pg}</option>
                        ))}
                      </select>
                      <button onClick={() => handleEditSave(defectId, 'generatedParentGroup')}>✓</button>
                      <button onClick={handleEditCancel}>✕</button>
                    </div>
                  ) : (
                    <span 
                      className="editable-cell suggested"
                      onClick={() => handleEditStart(defectId, 'generatedParentGroup', defect.generatedParentGroup)}
                    >
                      {defect.generatedParentGroup || '-'}
                    </span>
                  )}
                </td>

                {/* Etiketler - Düzenlenebilir */}
                <td>
                  <TagEditor
                    tags={tags}
                    onUpdate={(newTags) => {
                      if (groupByDescription && defect.count > 1 && onBulkSuggestionsUpdate) {
                        handleBulkTagUpdate(defect.description, newTags);
                      } else {
                        handleTagUpdate(defectId, newTags);
                      }
                    }}
                    editable={true}
                  />
                </td>

                {/* Tarih */}
                <td>{formatDate(defect.generatedAt || defect.startedAtDate)}</td>

                {/* Doğrulama */}
                <td>
                  <div className="validation-buttons">
                    {groupByDescription && defect.count > 1 ? (
                      <>
                        <button
                          className={`btn-validation btn-approve ${defect.validation === true ? 'active' : ''}`}
                          onClick={() => onBulkValidationChange?.(defect.description, true)}
                          title="Tümünü Onayla"
                        >
                          ✓ Tümü
                        </button>
                        <button
                          className={`btn-validation btn-reject ${defect.validation === false ? 'active' : ''}`}
                          onClick={() => onBulkValidationChange?.(defect.description, false)}
                          title="Tümünü Reddet"
                        >
                          ✕ Tümü
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className={`btn-validation btn-approve ${defect.validation === true ? 'active' : ''}`}
                          onClick={() => onValidationChange(defectId, true)}
                          title="Onayla"
                        >
                          ✓
                        </button>
                        <button
                          className={`btn-validation btn-reject ${defect.validation === false ? 'active' : ''}`}
                          onClick={() => onValidationChange(defectId, false)}
                          title="Reddet"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </td>

                {/* Silme (sadece admin) */}
                {userRole === 'admin' && (
                  <td>
                    <button
                      className="btn-delete"
                      onClick={() => onDelete?.(defectId)}
                      title="Sil"
                    >
                      🗑️
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {groupedDefects.length === 0 && (
        <div className="no-data">
          <p>📭 Gösterilecek kayıt bulunamadı</p>
        </div>
      )}
    </div>
  );
};

export default DefectsTable;


